import { useState, useEffect, useRef } from 'react'
import { Layout as AntLayout, Menu, Avatar, Dropdown, Badge, Button, Tooltip, Popover, notification } from 'antd'
import {
    LogoutOutlined,
    SunOutlined,
    MoonOutlined,
    BellOutlined,
    BellFilled,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    SendOutlined,
} from '@ant-design/icons'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { useNotificationStore } from '../../store/notificationStore'
import { useLeadChatEventsStore } from '../../store/leadChatEventsStore'
import { NotificationList } from '../Notifications/NotificationList'
import { useTheme } from '../../contexts/ThemeContext'
import { usePageSearchStore } from '../../store/pageSearchStore'
import { authApi, internalChatsApi } from '../../services/api'
import { managesSettings, roleLabel, seesAllLeads } from '../../constants/roles'
import {
    DashboardIcon,
    ChatsIcon,
    ContactsIcon,
    OperatorsIcon,
    TelegramAccountIcon,
    ProxyIcon,
    SettingsIcon,
    SendIcon,
    TasksIcon,
    // РАЗДЕЛ ОТКЛЮЧЁН: иконка «Календарь» пока не используется.
    // CalendarIcon,
} from '../Icons'

const { Sider, Header, Content } = AntLayout

interface LayoutProps {
    children: React.ReactNode
}


// 1 новое сообщение, 2 новых сообщения, 5 новых сообщений.
function pluralMessages(count: number): string {
    const tail = count % 100
    if (tail >= 11 && tail <= 14) return 'новых сообщений'
    if (count % 10 === 1) return 'новое сообщение'
    if (count % 10 >= 2 && count % 10 <= 4) return 'новых сообщения'
    return 'новых сообщений'
}

export default function Layout({ children }: LayoutProps) {
    const [collapsed, setCollapsed] = useState(false)
    const [notificationOpen, setNotificationOpen] = useState(false)
    const [unreadChatCount, setUnreadChatCount] = useState(0)
    const [notificationsEnabled, setNotificationsEnabled] = useState(() =>
        localStorage.getItem('tg_notifications_enabled') !== 'false'
    )
    const notificationsEnabledRef = useRef(notificationsEnabled)
    const navigate = useNavigate()
    const location = useLocation()
    const { user, logout, stopImpersonation } = useAuthStore()
    const { unreadCount, tgUnreadCount, fetchUnreadCount, addNotification, addTgMessage } = useNotificationStore()
    const { theme, toggleTheme } = useTheme()
    const resetSearch = usePageSearchStore((state) => state.reset)
    const [api, contextHolder] = notification.useNotification()

    useEffect(() => {
        notificationsEnabledRef.current = notificationsEnabled
    }, [notificationsEnabled])

    // Переход в другой раздел закрывает поиск: строка из предыдущего раздела
    // почти всегда не то, что нужно в новом. Сравниваем только раздел: у
    // каждого диалога свой адрес /chats/<id>, и поиск не должен сбрасываться
    // при каждом клике по чату.
    const section = location.pathname.split('/')[1] || ''
    useEffect(() => {
        resetSearch()
    }, [section, resetSearch])

    useEffect(() => {
        fetchUnreadCount()
        fetchUnreadChats()


        // Пока WebSocket жив, опрос не нужен: события приходят мгновенно, и
        // хватает редкой сверки на случай пропущенного push. Как только сокет
        // отвалился - переходим на частый опрос, иначе уведомления задержатся
        // до его восстановления.
        const wsAlive = { current: false }
        let tick = 0
        const interval = setInterval(() => {
            tick += 1
            const everyNthTick = wsAlive.current ? 6 : 1
            if (tick % everyNthTick !== 0) return
            fetchUnreadCount()
            fetchUnreadChats()
        }, 10000)


        const token = localStorage.getItem('token')
        let ws: WebSocket | null = null
        let reconnectTimeout: ReturnType<typeof setTimeout> | null = null
        let reconnectDelay = 1000
        let wsIsClosed = false

        const connectWs = () => {
            if (!token || wsIsClosed) return
            const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
            const wsUrl = `${wsProtocol}//${window.location.host}/ws?token=${token}`

            ws = new WebSocket(wsUrl)
            ws.onopen = () => {
                reconnectDelay = 1000
                wsAlive.current = true
            }

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data)
                    if (data.type === 'notification') {
                        addNotification(data.data)
                        if (notificationsEnabledRef.current) {
                            api.info({
                                message: data.data.title || 'Новое уведомление',
                                description: data.data.message,
                                placement: 'topRight',
                                duration: 3,
                                style: { width: 280 },
                            })
                        }
                    } else if (data.type === 'chat_duplicate_checked' && data.chat_id) {
                        useLeadChatEventsStore.getState().bumpDuplicateCheck(Number(data.chat_id))
                    } else if (data.type === 'new_message') {
                        fetchUnreadChats()

                        // Догрузка после обрыва связи присылает одно уведомление
                        // на диалог с числом пропущенных сообщений.
                        const caughtUp = Number(data.message?.caught_up_count || 0)
                        const preview = caughtUp > 1
                            ? `${caughtUp} ${pluralMessages(caughtUp)}: ${data.message.text || '📎 Медиа'}`
                            : (data.message?.text || '📎 Медиа')

                        if (data.message && !data.message.outgoing && data.db_chat_id) {
                            useLeadChatEventsStore.getState().bumpChatUnread(
                                Number(data.db_chat_id),
                                preview,
                            )
                        }

                        // Пользователя берём из хранилища на момент события, а не
                        // из замыкания: обработчик создаётся один раз при монтировании,
                        // и если тогда профиль ещё не загрузился, сравнение навсегда
                        // оставалось ложным - уведомления переставали приходить.
                        const currentUser = useAuthStore.getState().user
                        const isMyAccount = currentUser && data.operator_id === currentUser.id;

                        if (data.message && !data.message.outgoing && isMyAccount) {

                            const accountId = data.account_id
                            const chatId = data.chat_id
                            addTgMessage({
                                id: Date.now(),
                                message_id: data.message.id,
                                account_id: accountId,
                                chat_id: chatId,
                                topic_id: data.topic_id ?? data.message.topic_id,
                                lead_id: data.lead_id ?? null,
                                sender: data.message.from_user?.first_name || 'TG',
                                text: preview,
                                date: data.message.date,
                            })

                            if (notificationsEnabledRef.current) {
                                const senderName = data.message.from_user?.first_name || 'Сообщение'
                                const msgPreview = preview
                                api.open({
                                    key: `tg_msg_${data.message.id}`,
                                    message: senderName,
                                    description: msgPreview,
                                    placement: 'topRight',
                                    duration: 3,
                                    style: { width: 260, cursor: 'pointer' },
                                    onClick: () => {
                                        // Уведомление ведёт в диалог с лидом. Если лида
                                        // ещё нет, открываем раздел чатов как раньше.
                                        if (data.lead_id) {
                                            navigate(`/chats?leadId=${data.lead_id}`)
                                        } else if (chatId) {
                                            const params = new URLSearchParams()
                                            params.set('chat', String(chatId))
                                            if (accountId) params.set('account', String(accountId))
                                            if (data.message.id) params.set('messageId', String(data.message.id))
                                            if (data.topic_id ?? data.message.topic_id) params.set('topicId', String(data.topic_id ?? data.message.topic_id))
                                            navigate(`/chats?${params.toString()}`)
                                        }
                                        api.destroy(`tg_msg_${data.message.id}`)
                                    },
                                })
                            }
                        }
                    } else if (data.type === 'chat_message' || data.type === 'chat_update') {
                        fetchUnreadChats()
                    }
                } catch (e) {
                    console.error('WS parse error:', e)
                }
            }

            ws.onclose = () => {
                wsAlive.current = false
                if (!wsIsClosed) {
                    reconnectTimeout = setTimeout(() => {
                        reconnectDelay = Math.min(reconnectDelay * 2, 30000)
                        connectWs()
                    }, reconnectDelay)
                }
            }
            ws.onerror = () => { ws?.close() }
        }

        connectWs()

        return () => {
            clearInterval(interval)
            wsIsClosed = true
            if (reconnectTimeout) clearTimeout(reconnectTimeout)
            if (ws) ws.close()
        }
    }, [])

    const fetchUnreadChats = async () => {
        try {
            const res = await internalChatsApi.getMyChats()
            if (res.data) {
                const count = res.data.reduce((acc: number, chat: any) => acc + chat.unread_count, 0)
                setUnreadChatCount(count)
            }
        } catch (e) {
            console.error('Failed to fetch unread chats count', e)
        }
    }

    const isAdmin = seesAllLeads(user?.role)
    const isSuperAdmin = user?.role === 'superadmin'
    const isDark = theme === 'dark'

    // Порядок разделов задан эталонным макетом: работа начинается с чатов,
    // затем карточки, очередь, аналитика и настройки.
    const menuItems = [
        {
            key: '/chats',
            icon: <ChatsIcon size={20} />,
            label: 'Чаты',
        },
        {
            key: '/tasks',
            icon: <TasksIcon size={20} />,
            label: 'Задачи',
        },
        ...(user?.can_view_lead_cards === false ? [] : [{
            key: '/contacts',
            icon: <ContactsIcon size={20} />,
            label: 'Карточки лидов',
        }]),
        // Разделы, которые тимлид может убрать у операторов в настройках.
        ...(user?.can_view_duplicates === false ? [] : [{
            key: '/duplicates',
            icon: <ContactsIcon size={20} />,
            label: 'Дубли',
        }]),
        ...(user?.can_view_queue === false ? [] : [{
            key: '/broadcast-queue',
            icon: <SendOutlined style={{ fontSize: 18 }} />,
            label: 'Очередь',
        }]),
        ...(user?.can_view_dashboard === false ? [] : [{
            key: '/',
            icon: <DashboardIcon size={20} />,
            label: 'Дашборды',
        }]),
        // Пункт виден только тем, у кого есть право на настройки. Видеть всех
        // лидов для этого мало: у Старшего ЛМ раздел открывался бы пустым.
        // Свою учётку каждый открывает через меню профиля.
        ...(managesSettings(user?.role) ? [{
            key: '/settings',
            icon: <SettingsIcon size={20} />,
            label: 'Настройки',
        }] : []),
        // РАЗДЕЛ ОТКЛЮЧЁН вместе со статусной моделью: «Календарь».
        // {
        //     key: '/calendar',
        //     icon: <CalendarIcon size={20} />,
        //     label: 'Календарь',
        // },
    ]

    const adminItems = isAdmin ? [
        { type: 'divider' as const },
        {
            key: '/admin/operators',
            icon: <OperatorsIcon size={20} />,
            label: 'Пользователи',
        },
        ...(isSuperAdmin ? [
            {
                key: '/admin/accounts',
                icon: <TelegramAccountIcon size={20} />,
                label: 'TG Аккаунты',
            },
            {
                key: '/admin/proxies',
                icon: <ProxyIcon size={20} />,
                label: 'Прокси',
            },
        ] : []),
    ] : []

    const allMenuItems = [...menuItems, ...adminItems]

    const userMenuItems = [
        {
            key: 'account',
            icon: <SettingsIcon size={16} />,
            label: 'Учётная запись',
        },
        {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: 'Выйти',
            danger: true,
        },
    ]

    const handleUserMenu = ({ key }: { key: string }) => {
        if (key === 'logout' && user?.impersonated_by_id) {
            // Под чужим входом «Выйти» - это возврат в свою учётку: настоящего
            // сотрудника не выкидываем.
            stopImpersonation()
        } else if (key === 'logout') {
            // Сначала отзываем токены на сервере: копия токена (с другого
            // устройства, из логов) после выхода работать не должна. Выходим
            // из интерфейса в любом случае, даже если сервер не ответил.
            authApi.logout().catch(() => undefined).finally(() => {
                logout()
                navigate('/login')
            })
        } else if (key === 'account') {
            navigate('/account')
        }
    }

    // Каркас эталонного макета: горизонтальное верхнее меню вместо бокового,
    // высота 52px, разделы — «таблетками» в общей группе.
    const navPill = (active: boolean) => ({
        padding: '6px 9px',
        borderRadius: 5,
        fontSize: 14,
        cursor: 'pointer',
        whiteSpace: 'nowrap' as const,
        flex: 'none' as const,
        background: active ? 'oklch(0.28 0.015 260)' : 'transparent',
        color: active ? 'oklch(0.95 0.01 260)' : 'oklch(0.65 0.01 260)',
        fontWeight: active ? 600 : 500,
    })

    const isActive = (key: string) => key === '/' ? location.pathname === '/' : location.pathname.startsWith(key)

    return (
        <div style={{
            height: '100vh',
            width: '100%',
            maxWidth: '100vw',
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--bg-base)',
            color: 'var(--text-primary)',
            overflow: 'hidden',
        }}>
            {user?.impersonated_by_id && (
                // «Админ разработки» работает под сотрудником: видно всегда,
                // чтобы не забыть, от чьего имени уходят действия.
                <div style={{
                    flex: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 12,
                    flexWrap: 'wrap',
                    padding: '6px 16px',
                    background: '#ad6800',
                    color: '#fff',
                    fontSize: 13,
                }}>
                    <span>
                        Вы вошли как <b>{user.full_name || user.username}</b> ({roleLabel(user.role)}).
                        Все действия — от его имени.
                    </span>
                    <Button size="small" onClick={() => stopImpersonation()}>
                        Вернуться в свою учётку
                    </Button>
                </div>
            )}
            <div style={{
                height: 52,
                flex: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '0 14px',
                borderBottom: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                minWidth: 0,
            }}>
                <div style={{
                    fontWeight: 700,
                    fontSize: 16,
                    letterSpacing: '0.02em',
                    flex: 'none',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                }} onClick={() => navigate('/')}>
                    ОРКЕСТРАТОР
                </div>

                <div style={{
                    display: 'flex',
                    background: 'var(--bg-base)',
                    borderRadius: 7,
                    padding: 2,
                    gap: 2,
                    flex: 1,
                    minWidth: 0,
                    overflowX: 'auto',
                }}>
                    {[...menuItems, ...adminItems]
                        .flatMap((item) => (item as { key?: string; label?: string }).key
                            ? [{ key: (item as { key: string }).key, label: String((item as { label?: string }).label || '') }]
                            : [])
                        .map((item) => (
                            <div key={item.key} style={navPill(isActive(item.key))} onClick={() => navigate(item.key)}>
                                {item.label}
                            </div>
                        ))}
                </div>

                {/* Слот страницы «Чаты»: панель личных папок выводится сюда порталом. */}
                <div id="chat-header-slot" style={{ display: 'flex', alignItems: 'center', flex: 'none', minWidth: 0 }} />

                <Tooltip title={isDark ? 'Светлая тема' : 'Тёмная тема'}>
                    <div onClick={toggleTheme} style={{
                        width: 32, height: 32, borderRadius: 7, display: 'flex', alignItems: 'center',
                        justifyContent: 'center', cursor: 'pointer', background: 'var(--bg-base)', flex: 'none',
                    }}>
                        {isDark ? <SunOutlined /> : <MoonOutlined />}
                    </div>
                </Tooltip>


                <Popover
                    content={<NotificationList onClose={() => setNotificationOpen(false)} />}
                    trigger="click"
                    open={notificationOpen}
                    onOpenChange={setNotificationOpen}
                    placement="bottomRight"
                >
                    <Badge count={unreadCount + tgUnreadCount} size="small">
                        <div style={{
                            width: 32, height: 32, borderRadius: 7, display: 'flex', alignItems: 'center',
                            justifyContent: 'center', cursor: 'pointer', background: 'var(--bg-base)', fontSize: 15,
                        }}>🔔</div>
                    </Badge>
                </Popover>

                <Tooltip title={notificationsEnabled ? 'Выключить звук уведомлений' : 'Включить звук уведомлений'}>
                    <div
                        onClick={() => {
                            const next = !notificationsEnabled
                            setNotificationsEnabled(next)
                            localStorage.setItem('tg_notifications_enabled', String(next))
                        }}
                        style={{
                            width: 32, height: 32, borderRadius: 7, display: 'flex', alignItems: 'center',
                            justifyContent: 'center', cursor: 'pointer', background: 'var(--bg-base)',
                            color: notificationsEnabled ? 'var(--warning)' : 'var(--text-muted)', flex: 'none',
                        }}
                    >
                        {notificationsEnabled ? <BellFilled /> : <BellOutlined />}
                    </div>
                </Tooltip>

                <Dropdown menu={{ items: userMenuItems, onClick: handleUserMenu }} placement="bottomRight">
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                        flex: 'none', paddingLeft: 4,
                    }}>
                        <Avatar size={28} src={user?.avatar_url} style={{ background: 'var(--brand-primary)', fontSize: 12 }}>
                            {!user?.avatar_url && (user?.full_name || user?.username || 'U')[0].toUpperCase()}
                        </Avatar>
                        <div style={{ lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                            <div style={{ fontSize: 13, fontWeight: 600 }}>{user?.full_name || user?.username}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                {roleLabel(user?.role)}
                            </div>
                        </div>
                    </div>
                </Dropdown>
            </div>

            <div style={{ flex: 1, minHeight: 0, overflow: 'auto', background: 'var(--bg-base)' }}>
                {children}
            </div>

            {contextHolder}
        </div>
    )
}
